import { BOOKING_FLOW_REDESIGN_FLAG } from '@/constants/experimentConstants';
import { allowTracking } from '@/helpers/general';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { useConfidenceFlag } from '@/hooks/useConfidenceFlag';
import { useEffect } from 'react';

// Module-scoped, not a ref: usePlacePage, Services, ServiceReadMoreModal and the booking-flow
// route entry all call this hook independently and can mount in the same page load, before any
// of their dispatches has reached `state.flags` — a per-instance ref can't see across them, so
// each one would otherwise persist its own redundant SET_FLAGS.
let hasDispatchedResolution = false;

// Test-only escape hatch: the guard above is module state, so it survives across `it()` blocks
// in the same file (Jest doesn't reset modules between tests). Call this in a test's
// beforeEach/afterEach to isolate cases that assert on the dispatch.
export const __resetBookingFlowRedesignDispatchGuardForTests = () => {
  hasDispatchedResolution = false;
};

/**
 * The one shared resolution of the `booking-flow-redesign` flag (KTD7): reads the
 * persisted/SSR value from `state.flags` when present (and then never fetches), otherwise
 * fetches once and persists the result so every later call site — step pages and place-page
 * entry points — reads the same resolution. `enabled` is false until resolved, so a click
 * that lands before resolution falls back to control.
 */
export function useBookingFlowRedesign(): { enabled: boolean; isResolved: boolean } {
  const dispatch = useAppDispatch();

  const persistedFlag = useAppSelector((state) => state.flags?.[BOOKING_FLOW_REDESIGN_FLAG.name]);
  const hasPersisted = persistedFlag !== undefined;

  const { flagValue, isReady } = useConfidenceFlag(
    BOOKING_FLOW_REDESIGN_FLAG.name,
    BOOKING_FLOW_REDESIGN_FLAG.defaultValue,
    !hasPersisted,
  );

  useEffect(() => {
    // Persist only a resolution Confidence actually produced. Without tracking consent,
    // isReady is true immediately with the fail-closed default — persisting that would
    // lock a visitor who later accepts tracking into a fabricated control resolution.
    if (hasDispatchedResolution || hasPersisted || !isReady || !allowTracking()) return;
    hasDispatchedResolution = true;
    dispatch({ type: 'flags/SET_FLAGS', payload: { [BOOKING_FLOW_REDESIGN_FLAG.name]: { payload: flagValue } } });
  }, [isReady, flagValue, dispatch, hasPersisted]);

  const isResolved = hasPersisted || isReady;
  const enabled = hasPersisted
    ? Boolean((persistedFlag as { payload?: { enabled?: boolean } })?.payload?.enabled)
    : Boolean(flagValue?.enabled);

  return { enabled: isResolved && enabled, isResolved };
}

export default useBookingFlowRedesign;
